import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, PackagePlus, Plus, Search, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const STATUS = { DRAFT: ["In attesa", "bg-amber-100 text-amber-800"], READY: ["Pronta per il ritiro", "bg-emerald-100 text-emerald-800"], DONE: ["Evasa", "bg-emerald-100 text-emerald-800"], REJECTED: ["Rifiutata", "bg-red-100 text-red-800"] };
const fmt = (s) => new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const Picker = ({ catalog, quick, picked, onPick }) => {
  const [q, setQ] = useState("");
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = words.length ? catalog.filter((c) => words.every((w) => `${c.code} ${c.name}`.toLowerCase().includes(w))).slice(0, 30) : quick;
  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="h-11 pl-9 text-base" placeholder="Cerca e aggiungi più articoli (codice o nome)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tech-request-search" />
      </div>
      {!words.length && quick.length > 0 && <div className="micro-label mt-2">Articoli del tuo furgone · tocca per aggiungere</div>}
      {shown.length > 0 && (
        <div className="mt-1 max-h-56 overflow-y-auto border" data-testid="tech-request-results">
          {shown.map((c) => {
            const on = picked.has(c.id);
            return (
              <button key={c.id} type="button" onClick={() => onPick(c)} className={`flex w-full items-center gap-2 border-b px-3 py-2.5 text-left text-sm last:border-b-0 ${on ? "bg-emerald-50" : "hover:bg-accent"}`} data-testid={`tech-request-pick-${c.code}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center border ${on ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-400"}`}>{on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}</span>
                <span><span className="font-mono text-xs">{c.code}</span> · {c.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RequestForm = ({ onSent }) => {
  const [catalog, setCatalog] = useState([]);
  const [quick, setQuick] = useState([]);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/tech/van").then((r) => {
      setCatalog(r.data.catalog);
      const ids = new Set([...(r.data.frequent || []), ...r.data.vans.flatMap((v) => v.stock.map((s) => s.item_id))]);
      setQuick(r.data.catalog.filter((c) => ids.has(c.id)).slice(0, 40));
    }).catch(() => {});
  }, []);
  const add = (c) => setLines(lines.some((l) => l.item.id === c.id) ? lines.filter((l) => l.item.id !== c.id) : [...lines, { item: c, qty: 1 }]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tech/requests", { notes, lines: lines.map((l) => ({ item_id: l.item.id, qty: Number(l.qty) })) });
      toast.success(`Richiesta ${data.number} inviata al magazzino`);
      setLines([]); setNotes(""); onSent();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="tech-request-form">
      <Picker catalog={catalog} quick={quick} picked={new Set(lines.map((l) => l.item.id))} onPick={add} />
      {lines.length > 0 && <div className="micro-label" data-testid="tech-request-count">Nella richiesta · {lines.length} {lines.length === 1 ? "articolo" : "articoli"}</div>}
      {lines.map((l, i) => (
        <div key={l.item.id} className="flex items-center gap-2 border-b pb-2 text-sm" data-testid={`tech-request-line-${l.item.code}`}>
          <span className="flex-1"><span className="font-mono text-xs">{l.item.code}</span> · {l.item.name}</span>
          <Input type="number" min="1" step={l.item.serialized ? 1 : "any"} className="h-10 w-20" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} data-testid={`tech-request-qty-${l.item.code}`} />
          <span className="w-8 text-xs text-muted-foreground">{l.item.unit}</span>
          <Button size="icon" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="Togli" data-testid={`tech-request-remove-${l.item.code}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Textarea className="min-h-20 text-base" placeholder="Note per il magazzino (facoltative)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="tech-request-notes" />
      <Button className="h-12 w-full text-base" disabled={busy || !lines.length || lines.some((l) => !(Number(l.qty) > 0))} onClick={send} data-testid="tech-request-send">Invia richiesta al magazzino{lines.length > 1 ? ` (${lines.length} articoli)` : ""}</Button>
    </div>
  );
};

const History = ({ list }) => (
  <div className="space-y-2" data-testid="tech-request-history">
    <div className="micro-label">Le mie richieste</div>
    {!list.length && <p className="text-sm text-muted-foreground">Nessuna richiesta.</p>}
    {list.map((r) => {
      const [label, cls] = STATUS[r.status === "DRAFT" && r.pickup_at ? "READY" : r.status] || [r.status, ""];
      return (
        <div key={r.id} className="border p-3 text-sm" data-testid={`tech-request-${r.number}`}>
          <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs">{r.number} · {fmt(r.created_at)}</span><span className={`px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid={`tech-request-status-${r.number}`}>{label}</span></div>
          <div className="mt-1 text-xs text-muted-foreground">{(r.status === "DONE" ? r.executed_lines : r.lines).map((l) => `${l.code} ${l.qty}`).join(" · ")}</div>
          {r.status === "DRAFT" && r.pickup_at && <div className="mt-1 text-xs font-semibold text-emerald-700" data-testid={`tech-request-pickup-${r.number}`}>Pronta: ritiro il {fmt(r.pickup_at)} presso {r.from_name}</div>}
          {r.reject_note && <div className="mt-1 text-xs text-red-700">{r.reject_note}</div>}
        </div>
      );
    })}
  </div>
);

export const TechRequest = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/tech/requests").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const pending = list.filter((r) => r.status === "DRAFT").length;
  return (
    <>
      <Button variant="outline" className="mb-3 h-12 w-full text-base" onClick={() => { load(); setOpen(true); }} data-testid="tech-request-open">
        <PackagePlus className="mr-2 h-5 w-5" />Richiedi al magazzino centrale{pending > 0 && <span className="ml-2 bg-amber-100 px-1.5 text-xs text-amber-800">{pending} in attesa</span>}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto" data-testid="tech-request-sheet">
          <SheetHeader><SheetTitle>Richiesta al magazzino centrale</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-6">
            <RequestForm onSent={() => { load(); onChanged?.(); }} />
            <History list={list} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
